import type { ReactNode } from "react";
import type { MilestoneItem } from "@wayflyer/flyui";

export type OnboardingStep = {
  id: string;
  label: string;
  description?: string | ReactNode;
  subtext?: string;
  onClick?: () => void;
  analyticsId?: string;
  substeps?: Array<{
    id: string;
    label: string;
    onClick?: () => void;
    analyticsId?: string;
  }>;
};

export function buildMilestones(
  steps: OnboardingStep[],
  completedStepIds?: string[],
  completedSubstepIds?: string[],
  currentSubstepId?: string
): MilestoneItem[] {
  const completedSteps = new Set(completedStepIds ?? []);
  const completedSubsteps = new Set(completedSubstepIds ?? []);
  const priorSubstepIds = getPriorSubstepIds(steps, currentSubstepId);

  return steps.map((step): MilestoneItem => {
    const baseProps = {
      id: step.id,
      label: step.label,
      isExpandable: true,
      slots: {
        description: step.description,
        subtext: step.subtext,
      },
    };

    if (step.substeps) {
      const mappedSubsteps = step.substeps.map((sub) => {
        const isCompleted = completedSubsteps.has(sub.id);
        const hasWarning = !isCompleted && priorSubstepIds.has(sub.id);
        return {
          id: sub.id,
          label: sub.label,
          isCompleted: isCompleted || hasWarning,
          ...(hasWarning ? { status: "warning" as const } : {}),
          ...addClickableProps(sub.onClick, sub.analyticsId),
        };
      });

      const allTrulyCompleted = step.substeps.every((sub) => completedSubsteps.has(sub.id));

      return {
        ...baseProps,
        isCompleted: allTrulyCompleted,
        substeps: mappedSubsteps,
      };
    }

    return {
      ...baseProps,
      isCompleted: completedSteps.has(step.id),
      ...addClickableProps(step.onClick, step.analyticsId),
    };
  });
}

function getPriorSubstepIds(steps: OnboardingStep[], currentSubstepId?: string): Set<string> {
  const prior = new Set<string>();
  if (!currentSubstepId) {
    return prior;
  }
  for (const step of steps) {
    if (!step.substeps) {
      continue;
    }
    for (const sub of step.substeps) {
      if (sub.id === currentSubstepId) {
        return prior;
      }
      prior.add(sub.id);
    }
  }
  return prior;
}

function addClickableProps(onClick?: () => void, analyticsId?: string) {
  return onClick ? { onClick, analyticsId } : {};
}
